import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import { Box } from '@mui/material';
import LangEnum from '../../../lang/LangEnum';
import { SatisfactionSurveyFrequencyType } from '../../../services/modules/satisfactionSurveySettings/satisfactionSurveyFrequencyType.enum';
import { useTranslation } from '../../translation/TranslationContext';
import useSatisfactionSurveyFrequencyFields, {
  MAX_FREQUENCY_LAST_DAYS,
  MIN_FREQUENCY_LAST_DAYS,
} from '../hooks/useSatisfactionSurveyFrequencyFields';
import { SatisfactionSurveySettingsSectionProps } from '../satisfactionSurveySettings.types';

const frequencyLabels: Record<SatisfactionSurveyFrequencyType, LangEnum> = {
  [SatisfactionSurveyFrequencyType.ALWAYS]: LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_ALWAYS,
  [SatisfactionSurveyFrequencyType.ORDER_COLLECTION]:
    LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_ORDER_COLLECTION,
  [SatisfactionSurveyFrequencyType.LAST_DAYS]:
    LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_LAST_DAYS,
};

const SatisfactionSurveyFrequencyFields = ({
  settings,
  isDisabled,
  isSaving,
  rejectedSaves,
  onChange,
}: SatisfactionSurveySettingsSectionProps) => {
  const { translate } = useTranslation();
  const { isLastDays, lastDays, isOutOfRange, onFrequencyTypeChange, onLastDaysBlur } =
    useSatisfactionSurveyFrequencyFields({ settings, isSaving, rejectedSaves, onChange });

  const rangeMessage = translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_RANGE_ERROR, {
    min: MIN_FREQUENCY_LAST_DAYS,
    max: MAX_FREQUENCY_LAST_DAYS,
  });

  const frequencyOptions = Object.values(SatisfactionSurveyFrequencyType).map((frequencyType) => ({
    value: frequencyType,
    label: translate(frequencyLabels[frequencyType]),
  }));

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Select
        sx={{ minWidth: 280 }}
        disabled={isDisabled}
        label={translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_LABEL)}
        helperText={translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_HELPER)}
        value={settings.frequencyType}
        options={frequencyOptions}
        onChange={(event) =>
          onFrequencyTypeChange(event.target.value as SatisfactionSurveyFrequencyType)
        }
      />

      {isLastDays && (
        <TextField
          type="number"
          disabled={isDisabled}
          error={isOutOfRange}
          sx={{ maxWidth: 180 }}
          label={translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_FREQUENCY_LAST_DAYS_LABEL)}
          helperText={rangeMessage}
          value={lastDays.value}
          slotProps={{ htmlInput: { min: MIN_FREQUENCY_LAST_DAYS, max: MAX_FREQUENCY_LAST_DAYS } }}
          onFocus={lastDays.onFocus}
          onChange={(event) => lastDays.setValue(event.target.value)}
          onBlur={onLastDaysBlur}
        />
      )}
    </Box>
  );
};

export default SatisfactionSurveyFrequencyFields;
